import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import getCameraPlugin from '../utils/cameraPlugin';
import axiosInstance from "../api/axiosInstance";
import '../styles/style.css';

const BottomNav = () => {
  const CameraPlugin = getCameraPlugin();

  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [trippinActive, setTrippinActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iconState, setIconState] = useState("plus");
  const holdTimeoutRef = useRef(null);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    setIsAndroid(Capacitor.getPlatform() === 'android');
    setIsIos(Capacitor.getPlatform() === 'ios');
  }, []);

  const handleNavigation = async () => {
    if (isAndroid || isIos) {
      await openNativeCamera();
    }
    else {
      navigate('/camera');
    }
  }

  const openNativeCamera = async () => {
    try {
      if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
        const result = await CameraPlugin.openCamera();
        
        if (!result || !result.fileUri) {
          console.error('Error: No fileUri received from android or iOS.');
          return;
        }

        if (result.isVideo) {
          navigate('/see-content', { state: { video: result.fileUri } });
        }
        else {
          navigate('/see-content', { state: { photo: result.fileUri } });
        }
      }
      else {
        console.log('Running on Web. Using default camera handling.');
        navigate('/camera');
      }
    } catch (error) {
      console.error('Error opening camera:', error);
    }
  };

  const checkTrippinStatus = async () => {
    try {
      const response = await axiosInstance.get('/trippins-status/check');
      setTrippinActive(response.data.active);
    } catch (error) {
      console.error("Error checking trippin status:", error);
    }
  };

  const handleHoldStart = () => {
    holdTimeoutRef.current = setTimeout(async () => {
      setIconState("airplane");

      setLoading(true);
      await checkTrippinStatus();
      setDialogOpen(true);
      setLoading(false);
    }, 1000);
  };

  const handleHoldEnd = () => {
    clearTimeout(holdTimeoutRef.current);

    if (!dialogOpen) {
      setIconState("plus");
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setIconState("plus");
  };

  const handleStartTrippin = async () => {
    try {
      await axiosInstance.post(`/trippins-status/start`);
      setTrippinActive(true);
      setDialogOpen(false);
    } catch (error) {
      console.error("Error starting trippin:", error);
    }
  };

  const handleStopTrippin = async () => {
    try {
      await axiosInstance.post(`/trippins-status/stop`);
      setTrippinActive(false);
      setDialogOpen(false);
    } catch (error) {
      console.error("Error stopping trippin:", error);
    }
  };

  return (
    <div className="bottom-nav">
      <nav className={isIos || isAndroid ? "native-bottom-nav" : ""}>
        <ul>
          <li className="left">
            <Link to="/home">
              <div className="icon">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 6V15H6V11C6 9.89543 6.89543 9 8 9C9.10457 9 10 9.89543 10 11V15H15V6L8 0L1 6Z"></path>
                </svg>
              </div>
            </Link>
          </li>
          <li className="left">
            <Link to="/flashes">
              <div className="icon">
                <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M194.82,496a18.36,18.36,0,0,1-18.1-21.53l0-.11L204.83,320H96a16,16,0,0,1-12.44-26.06L302.73,23a18.45,18.45,0,0,1,32.8,13.71c0,.3-.08.59-.13.89L307.19,192H416a16,16,0,0,1,12.44,26.06L209.24,489A18.45,18.45,0,0,1,194.82,496Z"></path>
                </svg>
              </div>
            </Link>
          </li>
          <li className="center">
            <button onClick={handleNavigation}>
              <span>
                <div
                  onPointerDown={handleHoldStart}
                  onPointerUp={handleHoldEnd}
                  onPointerLeave={handleHoldEnd}
                  className={`icon ${iconState}`}
                >
                  {iconState === "airplane" ? (
                    <img src="/assets/icons/airplane.png" alt="Airplane Icon" />
                  ) : (
                    <svg>
                      <use xlinkHref="#plus-icon"></use>
                    </svg>
                  )}
                </div>
              </span>
            </button>
          </li>
          <li className="right">
            <Link to="/saved">
              <div className="icon">
                <svg
                  viewBox="-4 0 30 30"
                  fill="none"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  xmlnssketch="http://www.bohemiancoding.com/sketch/ns"
                >
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <title>bookmark</title>
                    <desc>Created with Sketch Beta.</desc>
                    <defs></defs>
                    <g
                      id="Page-1"
                      stroke="none"
                      strokeWidth="1"
                      fill="none"
                      fillRule="evenodd"
                      sketchtype="MSPage"
                    >
                      <g
                        id="Icon-Set-Filled"
                        sketchtype="MSLayerGroup"
                        transform="translate(-419.000000, -153.000000)"
                        className="bookmark-icon"
                      >
                        <path
                          d="M437,153 L423,153 C420.791,153 419,154.791 419,157 L419,179 C419,181.209 420.791,183 423,183 L430,176 L437,183 C439.209,183 441,181.209 441,179 L441,157 C441,154.791 439.209,153 437,153"
                          id="bookmark"
                          sketchtype="MSShapeGroup"
                        ></path>
                      </g>
                    </g>
                  </g>
                </svg>
              </div>
            </Link>
          </li>
          <li className="right">
            <Link to="/profile">
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" id="user-icon" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            </Link>
          </li>
        </ul>

        {/* Hidden SVG for button */}
        <svg style={{ display: 'none' }}>
          <symbol id="plus-icon" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
            <path d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"></path>
          </symbol>
        </svg>
      </nav>

      {/* Dialog */}
      {dialogOpen && (
        <div className="dialog">
          <p>{trippinActive ? "Do you want to stop your current trippin?" : "Do you want to start a trippin?"}</p>
          <button 
            onClick={trippinActive ? handleStopTrippin : handleStartTrippin}
            style={{
              backgroundColor: "#2c3e50",
              color: "#ffffff",
            }}
          >
            {trippinActive ? "Stop" : "Start"}
          </button>
          <button onClick={handleDialogClose}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default BottomNav;
